import {Dialog, Button, Link, useResponsiveValue} from '@primer/react'
import {Banner} from '@primer/react/experimental'
import {useState} from 'react'
import type {ModelState} from '../../../types'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {PlaygroundTokenUsage} from './PlaygroundTokenUsage'

interface PlaygroundTokenUsageWidgetProps {
  modelState: ModelState
  position: number
  inRepoContext?: boolean
}

export function PlaygroundTokenUsageWidget({modelState, position, inRepoContext}: PlaygroundTokenUsageWidgetProps) {
  const {lastMessageInputTokens, totalInputTokens, lastMessageOutputTokens, totalOutputTokens} = modelState.tokenUsage
  const usageStats = modelState.usageStats
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const [isOpen, setIsOpen] = useState(false)
  const {models: modelStates} = usePlaygroundState()
  const isMobile = useResponsiveValue({narrow: true}, false)
  const hasMessages = modelState.messages.length > 0

  const renderBorders = modelStates.length > 1

  const handleOpen = () => {
    setIsOpen(!isOpen)
    sendClickAnalyticsEvent({
      category: 'github_models_playground',
      action: 'click_to_open_usage_info',
    })
  }

  if (!modelState.catalogData.capabilities?.tokenCounting) {
    return null
  }

  const mainText =
    hasMessages || inRepoContext ? (
      <div className="fgColor-muted text-small">
        Input: {lastMessageInputTokens}/{totalInputTokens} • Output: {lastMessageOutputTokens}/{totalOutputTokens} •{' '}
        {usageStats.lastMessageLatency}ms
      </div>
    ) : (
      <div className="fgColor-muted text-small">This playground has free rate limits</div>
    )

  const mobileText =
    hasMessages || inRepoContext ? (
      <div className="fgColor-muted text-small text-right lh-condensed">
        In: {lastMessageInputTokens}/{totalInputTokens}
        <br />
        Out: {lastMessageOutputTokens}/{totalOutputTokens}
      </div>
    ) : (
      <div className="fgColor-muted text-small text-right lh-condensed">Free rate limits</div>
    )

  return (
    <>
      <Button
        {...testIdProps('playground-token-usage')}
        variant="invisible"
        size="small"
        aria-label="Show usage info"
        onClick={() => handleOpen()}
      >
        {isMobile ? mobileText : mainText}
      </Button>
      {isOpen && (
        <Dialog title="Model usage insights" role="dialog" onClose={() => setIsOpen(false)}>
          {!inRepoContext && (
            <>
              <Banner
                title="Info"
                hideTitle
                {...testIdProps('playground-free-rate-limits')}
                description={
                  <>
                    This playground has{' '}
                    <Link
                      href="https://docs.github.com/en/github-models/use-github-models/prototyping-with-ai-models#rate-limits"
                      inline
                    >
                      free rate limits
                    </Link>
                    .
                  </>
                }
              />
            </>
          )}
          <div className="d-flex gap-3">
            {modelStates.map(model => (
              <PlaygroundTokenUsage
                key={`pg-usage-${position}-${model.catalogData.id}`}
                modelState={model}
                border={renderBorders}
              />
            ))}
          </div>
        </Dialog>
      )}
    </>
  )
}

try{ PlaygroundTokenUsageWidget.displayName ||= 'PlaygroundTokenUsageWidget' } catch {}